<div
  class="max-w-2xl mx-auto space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500 p-1"
>
  <!-- Header -->
  <div class="text-center mb-8">
    <h2 class="text-2xl font-bold text-gray-800 mb-2">Edit Assignment</h2>
    <p class="text-gray-500">
      Review and edit the generated questions before finalizing.
    </p>
  </div>

  <!-- Assignment Title Card -->
  @if (title$ | async; as title) {
    <div class="title-card">
      <div class="flex items-start gap-3">
        <span
          class="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg mt-0.5"
          [style.backgroundColor]="themeColor + '14'"
        >
          <lucide-icon
            [img]="icons.FileText"
            class="w-4 h-4"
            [style.color]="themeColor"
          ></lucide-icon>
        </span>
        <div class="min-w-0 flex-1">
          <!-- Label + Info -->
          <div class="flex items-center justify-between gap-2 mb-1">
            <div class="flex items-center gap-1">
              <span class="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                Assignment Title
              </span>
              <button
                cdkOverlayOrigin
                #titleInfoTrigger="cdkOverlayOrigin"
                (click)="toggleTitleInfo()"
                class="inline-flex items-center justify-center w-4 h-4 text-gray-300 hover:text-gray-500 transition-colors cursor-pointer"
              >
                <lucide-icon [img]="icons.Info" class="w-3 h-3"></lucide-icon>
              </button>
              <ng-template
                cdkConnectedOverlay
                [cdkConnectedOverlayOrigin]="titleInfoTrigger"
                [cdkConnectedOverlayOpen]="showTitleInfo"
                [cdkConnectedOverlayHasBackdrop]="true"
                cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
                [cdkConnectedOverlayPositions]="titleInfoPositions"
                (backdropClick)="closeTitleInfo()"
              >
                <div class="title-info-popover">
                  This title will be displayed to students
                </div>
              </ng-template>
            </div>
            @if (!isEditingTitle) {
              <button
                (click)="startTitleEdit(title)"
                class="edit-btn"
                [style.--theme-color]="themeColor"
              >
                <lucide-icon [img]="icons.Pencil" class="w-3 h-3"></lucide-icon>
                Edit
              </button>
            }
          </div>
          <!-- Content: display or edit -->
          @if (isEditingTitle) {
            <div class="space-y-3">
              <textarea
                #titleInput
                [value]="editingTitleValue"
                (input)="onTitleInput($event)"
                (keydown.escape)="cancelTitleEdit()"
                class="w-full text-sm font-semibold text-gray-900 bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:border-transparent resize-none overflow-hidden"
                [style]="'--tw-ring-color: ' + themeColor + '4D'"
                maxlength="200"
                rows="1"
              ></textarea>
              <div class="flex items-center gap-2">
                <button
                  (click)="cancelTitleEdit()"
                  class="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  (click)="confirmTitleEdit()"
                  class="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-medium text-white transition-colors cursor-pointer"
                  [style.backgroundColor]="themeColor"
                >
                  <lucide-icon [img]="icons.Check" class="w-4 h-4"></lucide-icon>
                  Save
                </button>
              </div>
            </div>
          } @else {
            <h2 class="text-sm font-semibold text-gray-900 leading-relaxed">
              {{ title }}
            </h2>
          }
        </div>
      </div>
    </div>
  }

  <!-- Questions List -->
  @if (questions$ | async; as questions) {
    @if (questions.length === 0) {
      <div
        class="bg-white p-8 rounded-2xl shadow-lg shadow-gray-100 border border-gray-100 text-center"
      >
        <p class="text-gray-500 mb-4">No questions generated yet.</p>
        <button
          (click)="toggleAddMenu()"
          class="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors cursor-pointer"
          [style.backgroundColor]="themeColor"
          [style.color]="'white'"
        >
          <lucide-icon [img]="icons.Plus" class="w-4 h-4"></lucide-icon>
          Add Question
        </button>
      </div>
    } @else {
      <!-- Question Cards -->
      @for (question of questions; track question.id; let idx = $index) {
        <div class="question-card">
          <!-- Question Header -->
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <span
                class="question-number"
                [style.backgroundColor]="themeColor"
              >
                {{ idx + 1 }}
              </span>
              <span class="question-type-badge">
                <lucide-icon
                  [img]="getQuestionTypeIcon(question.type)"
                  class="w-3 h-3"
                ></lucide-icon>
                {{ getQuestionTypeLabel(question.type) }}
              </span>
            </div>
            <button
              cdkOverlayOrigin
              #questionMenuTrigger="cdkOverlayOrigin"
              (click)="toggleQuestionMenu(idx)"
              class="option-menu-trigger"
            >
              <lucide-icon
                [img]="icons.MoreVertical"
                class="w-4 h-4"
              ></lucide-icon>
            </button>

            <ng-template
              cdkConnectedOverlay
              [cdkConnectedOverlayOrigin]="questionMenuTrigger"
              [cdkConnectedOverlayOpen]="questionMenuOpenIdx === idx"
              [cdkConnectedOverlayHasBackdrop]="true"
              cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
              [cdkConnectedOverlayPositions]="menuPositions"
              (backdropClick)="closeQuestionMenu()"
            >
              <div class="option-menu-popover">
                <button
                  class="option-menu-item"
                  (click)="openEditQuestion(question.id)"
                >
                  <lucide-icon
                    [img]="icons.Pencil"
                    class="w-4 h-4"
                  ></lucide-icon>
                  Edit
                </button>
                <button
                  class="option-menu-item"
                  [disabled]="idx === 0"
                  [class.option-menu-item-disabled]="idx === 0"
                  (click)="moveQuestion(idx, 'up')"
                >
                  <lucide-icon
                    [img]="icons.ChevronUp"
                    class="w-4 h-4"
                  ></lucide-icon>
                  Move up
                </button>
                <button
                  class="option-menu-item"
                  [disabled]="idx === questions.length - 1"
                  [class.option-menu-item-disabled]="
                    idx === questions.length - 1
                  "
                  (click)="moveQuestion(idx, 'down')"
                >
                  <lucide-icon
                    [img]="icons.ChevronDown"
                    class="w-4 h-4"
                  ></lucide-icon>
                  Move down
                </button>
                <div class="option-menu-divider"></div>
                <button
                  class="option-menu-item option-menu-item-danger"
                  (click)="requestDeleteQuestion(question.id)"
                >
                  <lucide-icon
                    [img]="icons.Trash2"
                    class="w-4 h-4"
                  ></lucide-icon>
                  Delete
                </button>
              </div>
            </ng-template>
          </div>

          <!-- Question Text -->
          <p class="text-sm font-medium text-gray-800 leading-relaxed mb-3">
            {{ question.questionText || 'Untitled question' }}
          </p>

          <!-- Multiple Choice Options -->
          @if (question.type === QuestionType.MULTIPLE_CHOICE) {
            <div class="grid grid-cols-1 gap-1.5">
              @for (
                option of question.options;
                track option.id;
                let optIdx = $index
              ) {
                <div
                  class="option-row"
                  [class.option-correct]="option.isCorrect"
                  [class.option-default]="!option.isCorrect"
                >
                  <span
                    class="option-letter"
                    [class.option-letter-correct]="option.isCorrect"
                    [class.option-letter-default]="!option.isCorrect"
                  >
                    {{ getOptionLetter(optIdx) }}
                  </span>
                  <span
                    class="text-sm leading-snug"
                    [class.text-green-800]="option.isCorrect"
                    [class.font-medium]="option.isCorrect"
                    [class.text-gray-700]="!option.isCorrect"
                  >
                    {{ option.text }}
                  </span>
                  @if (option.isCorrect) {
                    <lucide-icon
                      [img]="icons.Check"
                      class="w-3.5 h-3.5 text-green-600 ml-auto shrink-0"
                    ></lucide-icon>
                  }
                </div>
              }
            </div>
          }

          <!-- Short Answer / Essay placeholder -->
          @if (
            question.type === QuestionType.SHORT_ANSWER ||
            question.type === QuestionType.ESSAY
          ) {
            <div
              class="py-3 px-4 bg-gray-50 rounded-lg border border-dashed border-gray-200"
            >
              <p class="text-xs text-gray-400 italic">
                @if (question.type === QuestionType.SHORT_ANSWER) {
                  Students will provide a short text answer
                } @else {
                  Students will write an essay response
                }
              </p>
            </div>
          }

          <!-- Card Actions -->
          <div class="flex items-center gap-2 mt-3">
            <button
              (click)="requestDeleteQuestion(question.id)"
              class="delete-btn flex-1"
            >
              <lucide-icon
                [img]="icons.Trash2"
                class="w-3 h-3"
              ></lucide-icon>
              Delete
            </button>
            <button
              (click)="openEditQuestion(question.id)"
              class="edit-btn flex-1"
              [style.--theme-color]="themeColor"
            >
              <lucide-icon
                [img]="icons.Pencil"
                class="w-3 h-3"
              ></lucide-icon>
              Edit
            </button>
          </div>
        </div>
      }
    }
  }

  <!-- Add Question Button -->
  <div class="relative">
    <button
      (click)="toggleAddMenu()"
      (blur)="closeAddMenu()"
      class="w-full py-2.5 text-sm text-gray-500 font-medium bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer flex items-center justify-center gap-2"
    >
      <lucide-icon [img]="icons.Plus" class="w-4 h-4"></lucide-icon>
      Add question
    </button>

    <!-- Add Question Menu -->
    @if (showAddMenu$ | async) {
      <div
        class="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-bottom-2 duration-200"
      >
        <button
          (mousedown)="addQuestion(QuestionType.ESSAY)"
          class="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 cursor-pointer"
        >
          <lucide-icon
            [img]="icons.FileText"
            class="w-5 h-5 text-gray-600"
          ></lucide-icon>
          <span class="font-medium text-gray-800">Add essay</span>
        </button>
        <button
          (mousedown)="addQuestion(QuestionType.MULTIPLE_CHOICE)"
          class="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 cursor-pointer"
        >
          <lucide-icon
            [img]="icons.CheckCircle"
            class="w-5 h-5 text-gray-600"
          ></lucide-icon>
          <span class="font-medium text-gray-800">Add multiple choice</span>
        </button>
        <button
          (mousedown)="addQuestion(QuestionType.SHORT_ANSWER)"
          class="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 cursor-pointer"
        >
          <lucide-icon
            [img]="icons.MessageSquare"
            class="w-5 h-5 text-gray-600"
          ></lucide-icon>
          <span class="font-medium text-gray-800">Add short answer</span>
        </button>
      </div>
    }
  </div>

  <!-- Error Message -->
  @if (error$ | async; as error) {
    <div
      class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
    >
      {{ error }}
    </div>
  }

  <!-- Spacer for fixed footer -->
  <div class="h-24"></div>
</div>

<!-- Fixed Footer -->
<div class="step-footer">
  <div class="max-w-2xl mx-auto flex gap-3 px-1">
    <button
      (click)="onBack()"
      class="flex items-center justify-center gap-2 px-6 py-3 text-gray-600 font-medium bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
    >
      <lucide-icon [img]="icons.ArrowLeft" class="w-4 h-4"></lucide-icon>
      Back
    </button>
    <button
      (click)="onPublish()"
      [disabled]="!(canPublish$ | async) || (isSaving$ | async)"
      class="flex-1 py-3 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      [style.backgroundColor]="themeColor"
    >
      @if (isSaving$ | async) {
        <span class="flex items-center justify-center gap-2">
          <div
            class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"
          ></div>
          Saving...
        </span>
      } @else {
        Continue
      }
    </button>
  </div>
</div>

<!-- Delete Confirmation Dialog -->
<app-confirm-dialog
  #confirmDialog
  title="Delete Question"
  message="Are you sure you want to delete this question? This action cannot be undone."
  confirmText="Delete"
  cancelText="Cancel"
  variant="danger"
  (confirmed)="confirmDeleteQuestion()"
  (cancelled)="cancelDeleteQuestion()"
></app-confirm-dialog>

<!-- Back Confirmation Dialog -->
<app-confirm-dialog
  #backConfirmDialog
  title="Go Back?"
  message="Going back will discard all current questions. You'll need to regenerate them from scratch. Are you sure you want to continue?"
  confirmText="Go Back"
  cancelText="Stay Here"
  variant="warning"
  (confirmed)="confirmBack()"
></app-confirm-dialog>

